import { useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { useDebounce } from "@/hooks/useDebounce";
import { CATEGORIES } from "@/lib/constants";

export interface ReceiptFilterOptions {
  searchQuery: string;
  category: string;
  dateRange: DateRange | undefined;
  confidenceLevel: string;
  expenseType: string;
}

interface ReceiptFiltersProps {
  filters: ReceiptFilterOptions;
  onFilterChange: (filters: ReceiptFilterOptions) => void;
  onReset: () => void;
}

// Use shared categories with "All Categories" filter option
const FILTER_CATEGORIES = ["All Categories", ...CATEGORIES];

export default function ReceiptFilters({ filters, onFilterChange, onReset }: ReceiptFiltersProps) {
  // Debounce search query to prevent excessive re-renders and API calls
  const debouncedSearchQuery = useDebounce(filters.searchQuery, 300);

  const hasActiveFilters = useMemo(() => 
    filters.searchQuery ||
    filters.category !== "All Categories" ||
    filters.dateRange ||
    filters.confidenceLevel !== "all" ||
    filters.expenseType !== "all",
    [filters]
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, searchQuery: e.target.value });
  }, [filters, onFilterChange]);

  const handleCategoryChange = useCallback((value: string) => {
    onFilterChange({ ...filters, category: value });
  }, [filters, onFilterChange]);

  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    onFilterChange({ ...filters, dateRange: range });
  }, [filters, onFilterChange]);

  const handleConfidenceChange = useCallback((value: string) => {
    onFilterChange({ ...filters, confidenceLevel: value });
  }, [filters, onFilterChange]);

  const handleExpenseTypeChange = useCallback((value: string) => {
    onFilterChange({ ...filters, expenseType: value });
  }, [filters, onFilterChange]);

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Filters</h3>
        {hasActiveFilters && (
          <Button
            onClick={onReset}
            variant="ghost"
            size="sm"
            className="h-8 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Search */}
        <div className="space-y-2">
          <Label htmlFor="search">Search Merchant</Label>
          <Input
            id="search"
            placeholder="Search by merchant name..."
            value={filters.searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Select
            value={filters.category}
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger id="category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Range */}
        <div className="space-y-2">
          <Label>Date Range</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.dateRange?.from ? (
                  filters.dateRange.to ? (
                    <>
                      {format(filters.dateRange.from, "LLL dd, y")} -{" "}
                      {format(filters.dateRange.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(filters.dateRange.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={filters.dateRange?.from}
                selected={filters.dateRange}
                onSelect={handleDateRangeChange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Confidence Level */}
        <div className="space-y-2">
          <Label htmlFor="confidence">OCR Confidence</Label>
          <Select
            value={filters.confidenceLevel}
            onValueChange={handleConfidenceChange}
          >
            <SelectTrigger id="confidence">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="high">High (80%+)</SelectItem>
              <SelectItem value="medium">Medium (60-79%)</SelectItem>
              <SelectItem value="low">Low (&lt;60%)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Expense Type */}
        <div className="space-y-2">
          <Label htmlFor="expense-type">Expense Type</Label>
          <Select
            value={filters.expenseType}
            onValueChange={handleExpenseTypeChange}
          >
            <SelectTrigger id="expense-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Expenses</SelectItem>
              <SelectItem value="business">Business Only</SelectItem>
              <SelectItem value="personal">Personal Only</SelectItem>
              <SelectItem value="deductible">Deductible Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  );
}
